import React from 'react';
import PropTypes from 'prop-types';
import {format} from 'date-fns';
import {useLocales} from 'packages/common/locales';
import {formatSlotLabel} from '../utils/stepSummary';
import {isLaunchWaitlistDay} from 'packages/common/utils/restaurant/preOpening';

// @mui
import {styled, alpha, useTheme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';

// icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import AddTaskIcon from '@mui/icons-material/AddTask';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// constants
import {SERVICE_TYPES, CONFIRMED_RESERVATION_STATUSES} from '../constants/reservation';

/**
 * @file Étape de confirmation finale du flow de réservation.
 * @module packages.common.components.table-reservation.sections.ConfirmationStep
 *
 * Récapitule la réservation enregistrée (service, date, créneau, convives,
 * coordonnées client, formules et demandes particulières) et confirme l'envoi
 * de l'email. Le rendu s'adapte à trois cas : réservation confirmée, liste
 * d'attente (`formData.isWaitlistReservation`) et demande en attente de validation
 * du restaurant (`formData.reservationStatus` non confirmé, quand la confirmation
 * automatique est désactivée). Icône, libellés et message d'information diffèrent
 * pour chacun.
 */

// ----------------------------------------------------------------------

const ConfirmationContainer = styled(Box)(({theme}) => ({
  padding: theme.spacing(2),
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));


const CompactInfoCard = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: 8,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
  width: '100%',
}));

const InfoItem = styled(Box)(({theme}) => ({
  'display': 'flex',
  'alignItems': 'center',
  'marginBottom': theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const InfoIcon = styled(Box)(({theme}) => ({
  width: 20,
  height: 20,
  marginRight: theme.spacing(1),
  color: theme.palette.grey[600],
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
}));

const ButtonsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  borderTop: `1px solid ${theme.palette.divider}`,
  width: '100%',
}));

const ActionButton = styled(Button)(({theme, color = 'primary'}) => ({
  'borderRadius': 8,
  'padding': theme.spacing(1, 3),
  'textTransform': 'none',
  'fontWeight': 600,
  'boxShadow': color === 'primary' ?
    `0 2px 6px ${alpha(theme.palette.primary.main, 0.2)}` :
    `0 1px 3px ${alpha(theme.palette.text.primary, 0.1)}`,
  'transition': theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: color === 'primary' ?
      `0 3px 8px ${alpha(theme.palette.primary.main, 0.25)}` :
      `0 2px 6px ${alpha(theme.palette.text.primary, 0.15)}`,
  },
}));

// ----------------------------------------------------------------------

/**
 * Affiche le récapitulatif final d'une réservation confirmée ou mise en liste d'attente.
 *
 * @param {Object} props
 * @param {Object} props.formData - Données de la réservation à récapituler (date, timeSlot, partySize, coordonnées, selectedFormulas, isWaitlistReservation, reservationStatus)
 * @param {Object} [props.config] - Configuration du restaurant
 * @param {Function} props.onReset - Réinitialise le parcours pour une nouvelle réservation
 * @param {Function} [props.onClose] - Ferme le widget (bouton affiché uniquement en mode flottant embarqué)
 * @param {boolean} [props.embedded] - Indique que le widget est embarqué dans une page hôte
 * @param {string} [props.displayMode] - Mode d'affichage du widget ('floating', etc.)
 * @param {string} [props.primaryColor] - Couleur d'accentuation de l'établissement, fallback sur la couleur primaire du thème
 * @param {Function} [props.t] - Fonction de traduction générique (fallback sur des libellés français)
 * @return {JSX.Element}
 */
export function ConfirmationStep({formData, config, onReset, onClose, embedded, displayMode, primaryColor, t}) {
  const theme = useTheme();

  const {currentLang} = useLocales();
  const dateFnsLocale = currentLang.adapterLocale;

  const serviceDisplay = formData.service === SERVICE_TYPES.LUNCH ?
    (t ? t('widgets.reservation.form.lunchService') : 'Midi') :
    (t ? t('widgets.reservation.form.dinnerService') : 'Soir');

  const isWaitlist = formData.isWaitlistReservation;
  // Liste d'attente de lancement d'un nouveau restaurant : pas de « place qui se libère ».
  const isLaunchWaitlist = isWaitlist && isLaunchWaitlistDay(config?.preOpening, formData.date);

  // Demande à valider par le restaurant : la confirmation automatique est
  // désactivée, le backend a créé la réservation en `pending` (ou un autre statut
  // non confirmé). On n'affiche « confirmée » que si le statut réel l'est. Statut
  // absent (widget servi depuis un cache antérieur) : on retombe sur le cas
  // confirmé pour ne pas casser le parcours nominal.
  const isPending = !isWaitlist &&
    !!formData.reservationStatus &&
    !CONFIRMED_RESERVATION_STATUSES.includes(formData.reservationStatus);

  const iconColor = primaryColor || theme.palette.primary.main;

  return (
    <Fade in timeout={400}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <ConfirmationContainer>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            gap: 1.5,
          }}>
            {isWaitlist || isPending ? (
              <HourglassEmptyIcon sx={{
                fontSize: 36,
                color: 'warning.main',
                flexShrink: 0,
              }} />
            ) : (
              <CheckCircleIcon sx={{
                fontSize: 36,
                color: 'success.main',
                flexShrink: 0,
              }} />
            )}
            <Box>
              <Typography variant="h6" sx={{fontWeight: 600, mb: 0.5}}>
                {isWaitlist ?
                  (t ? t('widgets.reservation.confirmation.waitlistConfirmed') : 'Liste d\'attente confirmée !') :
                  isPending ?
                    (t ? t('widgets.reservation.confirmation.pending') : 'Demande de réservation envoyée !') :
                    (t ? t('widgets.reservation.confirmation.confirmed') : 'Réservation confirmée !')
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatSlotLabel(formData.date, formData.timeSlot, {locale: dateFnsLocale, t})}
              </Typography>
            </Box>
          </Box>

          {/* Infos réservation en 2x2 */}
          <CompactInfoCard elevation={0}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, color: 'text.primary'}}>
              {isWaitlist ?
                (t ? t('widgets.reservation.confirmation.waitlistDetails') : 'Détails de l\'inscription') :
                isPending ?
                  (t ? t('widgets.reservation.confirmation.pendingDetails') : 'Détails de la demande') :
                  (t ? t('widgets.reservation.confirmation.reservationDetails') : 'Détails de la réservation')
              }
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <InfoItem>
                  <InfoIcon><RestaurantIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('widgets.reservation.confirmation.service') : 'Service'}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600}}>{serviceDisplay}</Typography>
                  </Box>
                </InfoItem>
              </Grid>

              <Grid item xs={6}>
                <InfoItem>
                  <InfoIcon><PeopleAltIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('widgets.reservation.confirmation.people') : 'Personnes'}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                      {formData.partySize} {formData.partySize === 1 ?
                        (t ? t('widgets.reservation.confirmation.person') : 'personne') :
                        (t ? t('widgets.reservation.confirmation.persons') : 'personnes')
                      }
                    </Typography>
                  </Box>
                </InfoItem>
              </Grid>

              <Grid item xs={6}>
                <InfoItem>
                  <InfoIcon><CalendarMonthIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('widgets.reservation.confirmation.date') : 'Date'}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                      {format(formData.date, 'd MMMM yyyy', {locale: dateFnsLocale})}
                    </Typography>
                  </Box>
                </InfoItem>
              </Grid>

              <Grid item xs={6}>
                <InfoItem>
                  <InfoIcon><AccessTimeIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t ? t('widgets.reservation.confirmation.time') : 'Heure'}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600}}>{formData.timeSlot}</Typography>
                  </Box>
                </InfoItem>
              </Grid>
            </Grid>
          </CompactInfoCard>

          {/* Infos client sur une ligne */}
          <CompactInfoCard elevation={0}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, color: 'text.primary'}}>
              {t ? t('widgets.reservation.confirmation.customerInfo') : 'Informations client'}
            </Typography>

            <Stack spacing={0.5}>
              <InfoItem>
                <InfoIcon><PersonIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                <Typography variant="body2" sx={{fontWeight: 600}}>
                  {formData.firstName} {formData.lastName}
                </Typography>
              </InfoItem>

              <InfoItem>
                <InfoIcon><EmailIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
                  {formData.email}
                </Typography>
              </InfoItem>

              <InfoItem>
                <InfoIcon><LocalPhoneIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                <Typography variant="body2">
                  {formData.phone}
                </Typography>
              </InfoItem>
            </Stack>
          </CompactInfoCard>

          {/* Formules sélectionnées */}
          {Array.isArray(formData.selectedFormulas) && formData.selectedFormulas.length > 0 && (
            <CompactInfoCard elevation={0}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, color: 'text.primary'}}>
                {t ? t('widgets.reservation.confirmation.formulas') : 'Vos choix'}
              </Typography>

              <Stack spacing={0.75}>
                {formData.selectedFormulas.map((formula, index) => {
                  const displayName = formula.name || (t ? t('widgets.reservation.confirmation.formulaFallback') : 'Formule');
                  const quantitySuffix = formula.quantity > 1 ? ` × ${formula.quantity}` : '';

                  return (
                    <InfoItem key={formula.formulaId || index}>
                      <InfoIcon><LocalDiningIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                      <Typography variant="body2" sx={{fontWeight: 600}}>
                        {displayName}{quantitySuffix}
                      </Typography>
                    </InfoItem>
                  );
                })}
              </Stack>
            </CompactInfoCard>
          )}

          {/* Détails supplémentaires compacts */}
          {(formData.occasion || formData.specialRequests) && (
            <CompactInfoCard elevation={0}>
              <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1, color: 'text.primary'}}>
                {t ? t('widgets.reservation.confirmation.additionalDetails') : 'Détails supplémentaires'}
              </Typography>

              {formData.occasion && (
                <InfoItem>
                  <InfoIcon><AddTaskIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                  <Typography variant="body2">
                    <strong>{t ? t('widgets.reservation.confirmation.occasion') : 'Occasion :'}</strong> {formData.occasion}
                  </Typography>
                </InfoItem>
              )}

              {formData.specialRequests && (
                <InfoItem sx={{alignItems: 'flex-start'}}>
                  <InfoIcon><AddTaskIcon sx={{fontSize: 16, color: iconColor}} /></InfoIcon>
                  <Typography variant="body2">
                    <strong>{t ? t('widgets.reservation.confirmation.requests') : 'Demandes :'}</strong> {formData.specialRequests}
                  </Typography>
                </InfoItem>
              )}
            </CompactInfoCard>
          )}

          {isWaitlist || isPending ? (
            <Alert
              severity="warning"
              icon={<HourglassEmptyIcon sx={{color: theme.palette.warning.dark}} />}
              sx={{
                'width': '100%',
                'mb': 1,
                'py': 1,
                'borderColor': theme.palette.warning.main,
                'backgroundColor': alpha(theme.palette.warning.main, 0.08),
                'justifyContent': 'center',
                '& .MuiAlert-message': {color: theme.palette.warning.dark, textAlign: 'center'},
              }}
              variant="outlined"
            >
              <Typography variant="body2" sx={{color: theme.palette.warning.dark}}>
                {isWaitlist ?
                  (t ?
                    t(isLaunchWaitlist ? 'widgets.reservation.preOpening.launchWaitlistConfirmed' : 'widgets.reservation.confirmation.waitlistMessage') :
                    'Vous êtes inscrit sur notre liste d\'attente. Si une place se libère, nous vous contacterons.') :
                  (t ?
                    t('widgets.reservation.confirmation.pendingMessage') :
                    'Votre demande de réservation a bien été envoyée. Elle sera confirmée par le restaurant, vous recevrez un email dès qu\'elle sera validée.')
                }
              </Typography>
            </Alert>
          ) : (
            <Alert
              severity="success"
              icon={<EmailIcon />}
              sx={{width: '100%', mb: 1, py: 1}}
              variant="outlined"
            >
              <Typography variant="body2">
                {t ?
                  t('widgets.reservation.confirmation.emailSent', {email: formData.email}) :
                  `Email de confirmation envoyé à ${formData.email}`
                }
              </Typography>
            </Alert>
          )}
        </ConfirmationContainer>

        <ButtonsContainer>
          <ActionButton
            variant="contained"
            onClick={onReset}
            startIcon={<RestartAltIcon />}
            sx={{'bgcolor': 'grey.800', '&:hover': {bgcolor: 'grey.700'}}}
          >
            {t ? t('widgets.reservation.confirmation.newReservation') : 'Nouvelle réservation'}
          </ActionButton>

          {onClose && embedded && displayMode === 'floating' && (
            <ActionButton
              variant="outlined"
              onClick={onClose}
              color="inherit"
            >
              {t ? t('widgets.reservation.confirmation.close') : 'Fermer'}
            </ActionButton>
          )}
        </ButtonsContainer>
      </Box>
    </Fade>
  );
}

ConfirmationStep.propTypes = {
  formData: PropTypes.object.isRequired,
  config: PropTypes.object,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  embedded: PropTypes.bool,
  displayMode: PropTypes.string,
  primaryColor: PropTypes.string,
  t: PropTypes.func,
};
